import { z } from 'zod';

// ZTX entity/relationship model (build-brief §4, SAD §2.3). Controls, techniques,
// countermeasures, risks and vulnerabilities are ENTITIES; the mappings between
// them are first-class RELATIONSHIPS carrying provenance. The analytical value is
// the graph — never reintroduce parallel, unrelated lists.

// Zero Trust pillars. AI_ML is reserved for the Phase 3 ATLAS/OWASP-LLM content;
// it is valid in the schema now even though the domain selector adds it later.
export const DOMAIN_IDS = [
  'GOVERNANCE',
  'NETWORK',
  'INFRASTRUCTURE',
  'APPLICATION',
  'IDENTITY',
  'AI_ML',
] as const;
export const domainIdSchema = z.enum(DOMAIN_IDS);
export type DomainId = z.infer<typeof domainIdSchema>;

export const entityKindSchema = z.enum([
  'control',
  'offensive-technique',
  'defensive-technique',
  'risk',
  'vulnerability',
]);
export type EntityKind = z.infer<typeof entityKindSchema>;

// Drives semantic colour: defence=cyan, threat=oxblood, connection=coral, impact=purple.
export const semanticRoleSchema = z.enum(['defence', 'threat', 'connection', 'impact']);
export type SemanticRole = z.infer<typeof semanticRoleSchema>;

export const relationshipTypeSchema = z.enum([
  'mitigates',
  'counters',
  'detects',
  'implemented-by',
  'maps-to',
  'enables',
  'exploits',
  'related',
]);
export type RelationshipType = z.infer<typeof relationshipTypeSchema>;

export const confidenceSchema = z.enum(['authoritative', 'community', 'unverified']);
export type Confidence = z.infer<typeof confidenceSchema>;

export const frameworkDefSchema = z.object({
  id: z.string().min(1), // 'nist-800-53-r5'
  label: z.string().min(1), // 'NIST 800-53 Rev 5'
  shortLabel: z.string().min(1), // 'NIST'
  entityKind: entityKindSchema,
  semanticRole: semanticRoleSchema,
  source: z.string().min(1), // canonical reference URL / citation
  version: z.string().min(1),
  // Scaffolded frameworks (no source data yet) are registered but flagged so the
  // UI can list them without implying populated content. See build-brief §6.
  populated: z.boolean().default(true),
});
export type FrameworkDef = z.infer<typeof frameworkDefSchema>;

export const entitySchema = z.object({
  id: z.string().min(1), // 'T1110', 'AC-3', 'D3-MFA', 'LLM01', 'AML.T0051', 'CVE-…'
  frameworkId: z.string().min(1),
  kind: entityKindSchema,
  name: z.string(),
  domains: z.array(domainIdSchema),
  tags: z.array(z.string()),
  metadata: z.record(z.unknown()),
});
export type Entity = z.infer<typeof entitySchema>;

export const relationshipSchema = z
  .object({
    sourceId: z.string().min(1),
    targetId: z.string().min(1),
    type: relationshipTypeSchema,
    source: z.string(), // provenance — citation for THIS mapping
    confidence: confidenceSchema,
  })
  // Provenance is a security property (SAD §8.1): any non-unverified mapping must
  // cite a source. Never present an invented mapping as authoritative.
  .refine((r) => r.confidence === 'unverified' || r.source.trim().length > 0, {
    message: 'authoritative/community relationships must carry a non-empty source',
    path: ['source'],
  });
export type Relationship = z.infer<typeof relationshipSchema>;

/** Validate arrays of entities/relationships; throws (fail loud) on malformed data. */
export function validateEntities(entities: unknown[]): Entity[] {
  return z.array(entitySchema).parse(entities);
}
export function validateRelationships(relationships: unknown[]): Relationship[] {
  return z.array(relationshipSchema).parse(relationships);
}
export function validateFrameworks(frameworks: unknown[]): FrameworkDef[] {
  return z.array(frameworkDefSchema).parse(frameworks);
}
